import { FC, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from 'hooks/auth'
import { useUserRole } from 'hooks/useUserRole'
import { isCategoryIntroPending } from '../utils/categoryIntro'
import { TmsPageIntroCard } from './TmsPageIntroCard'

interface PageTourInfo {
    title: string
    description: string
    videoId?: string
    videoUrl?: string
}

const PAGE_TOURS: Record<string, PageTourInfo> = {
    // TMS Dashboard (updater-agent)
    '/updater-agent': {
        title: 'Dashboard',
        description: 'Your command center — active load analytics, fleet metrics, and task management at a glance.',
        videoId: 'JyHINOplbB4',
    },
    '/updater-agent/task-management': {
        title: 'Task Management',
        description: 'Create and assign tasks to your team, set deadlines, and track progress across all operations.',
        videoId: '31Dqrvn9dnw',
        videoUrl: 'https://app.arcade.software/share/XW65Jw8YeILEtFc9N0JI?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/updater-agent/broker-performance': {
        title: 'Broker Performance',
        description: 'Review lane rates, on-time delivery, and rebooking rates per broker to make smarter load choices.',
        videoUrl: 'https://app.arcade.software/share/rk51TJR8DdVQlaYuUwbo?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/updater-agent/loads': {
        title: 'Ongoing Loads',
        description: 'Monitor all active loads in real time — location, ETA, broker updates, and delivery status.',
        videoUrl: 'https://app.arcade.software/share/6k8v1KvCD9LEy0bg74b2?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/updater-agent/scheduler/leaderboard': {
        title: 'Leaderboard',
        description: 'See top-performing drivers and dispatchers ranked by revenue, on-time delivery, and load count.',
        videoId: '4jvfaSdfVdM',
    },
    '/updater-agent/scheduler': {
        title: 'Dispatch Board',
        description: 'Assign loads to drivers, manage schedules, and track dispatch status across your fleet.',
        videoId: '31Dqrvn9dnw',
        videoUrl: 'https://app.arcade.software/share/f6G97li6imtwENzACgEQ?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },

    // Accounting
    '/accounting/invoices': {
        title: 'Invoices (AR)',
        description: 'Manage all outgoing invoices, track payments, and monitor outstanding balances in one place.',
        videoId: 'BAlMqFIM98w',
        videoUrl: 'https://app.arcade.software/share/9lGpiNcLM5pdEnrCjbQk?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/accounting/driver-pay': {
        title: 'Driver Pay',
        description: 'Calculate and process driver pay based on miles, loads, or percentage — automatically.',
        videoId: 'JyHINOplbB4',
        videoUrl: 'https://app.arcade.software/share/S0LD4IZC9B5Kk6kAUQCC?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/accounting/contractor-pay': {
        title: 'Contractor Pay',
        description: 'Manage settlements for owner-operators with automated pay calculations and history.',
        videoId: '31Dqrvn9dnw',
        videoUrl: 'https://app.arcade.software/share/baZ88N1uF51DA5h7nMlS?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    // Pay Adjustments replaced the separate Expenses and Prepayments pages; the two arcade
    // videos still describe the fuel/toll (ex-Expenses) and Other (ex-Prepayments) tabs.
    '/accounting/pay-adjustments': {
        title: 'Pay Adjustments',
        description: 'Track fuel, tolls, prepayments and other driver pay adjustments — expenses and payroll adjustments in one place.',
        videoId: 'rJ5CLe-Bd34',
        videoUrl: 'https://app.arcade.software/share/dFcMoo1ebhJfqQjiLvMU?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/accounting/factoring': {
        title: 'Factoring',
        description: 'Track factoring status, submitted invoices, and funding progress in one place.',
        videoUrl: 'https://app.arcade.software/share/DIweYupEPtPxqnsiQVeA?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },

    // Fleet
    '/fleet/overview': {
        title: 'Fleet Overview',
        description: 'Get a complete picture of your fleet — truck status, utilization, and availability at a glance.',
        videoId: 'JyHINOplbB4',
        videoUrl: 'https://app.arcade.software/share/BJEUdMGguBo2UtbWHG5i?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/fleet/compliance': {
        title: 'Fleet Compliance',
        description: 'Track IFTA, registration renewals, permits, and other compliance requirements across your fleet.',
        videoId: '31Dqrvn9dnw',
        videoUrl: 'https://app.arcade.software/share/LZdcKrnjpQdPwGM4wiSg?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/fleet/maintenance': {
        title: 'Maintenance',
        description: 'Schedule preventive maintenance, log repairs, and get alerts for upcoming service due dates.',
        videoId: 'rJ5CLe-Bd34',
        videoUrl: 'https://app.arcade.software/share/7Z6TwwHozu0z0FB9jEDg?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/fuel-ai': {
        title: 'Fuel AI',
        description: 'Optimize fuel usage, truck assignments, and Fuel AI actions from one place.',
        videoUrl: 'https://app.arcade.software/share/4oksCFAePIEq4c9ijX7R?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },

    // Safety
    '/safety/dashboard': {
        title: 'Safety Dashboard',
        description: 'Monitor compliance scores, upcoming inspections, accidents, and drug testing schedules.',
        videoId: 'JyHINOplbB4',
        videoUrl: 'https://app.arcade.software/share/HWYjgu1yxnyd8dYrP557?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/safety/driver-compliance': {
        title: 'Safety Compliance',
        description: 'Track DOT compliance requirements, CSA scores, and regulatory deadlines for your fleet.',
        videoId: '31Dqrvn9dnw',
        videoUrl: 'https://app.arcade.software/share/BTVQaEYV7wA5JXicT7Pq?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/safety/dot-inspections': {
        title: 'DOT Inspections',
        description: 'Log roadside inspection results, track violations, and manage corrective action plans.',
        videoId: 'rJ5CLe-Bd34',
        videoUrl: 'https://app.arcade.software/share/i9tPSy7U8U3IRnNppqm7?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/safety/accidents': {
        title: 'Accidents & Claims',
        description: 'Document accidents, manage insurance claims, and track resolution status in one place.',
        videoId: '4jvfaSdfVdM',
        videoUrl: 'https://app.arcade.software/share/9wpzuaEbdQV5Wrs07IRy?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/safety/drug-alcohol': {
        title: 'Drug & Alcohol',
        description: 'Manage your drug and alcohol testing program — scheduling, results, and clearinghouse reporting.',
        videoId: 'BAlMqFIM98w',
        videoUrl: 'https://app.arcade.software/share/2z2U7SbPjcA1ms9YyzIu?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/safety/ifta': {
        title: 'IFTA',
        description: 'Automate IFTA fuel tax calculations and generate quarterly reports for each jurisdiction.',
        videoId: 'JyHINOplbB4',
        videoUrl: 'https://app.arcade.software/share/08XOKJauFrQIgNjuEY9C?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },

    // Profiles
    '/profiles/drivers': {
        title: 'Drivers',
        description: 'Manage driver profiles, licenses, medical cards, HOS records, and performance history.',
        videoId: 'JyHINOplbB4',
        videoUrl: 'https://app.arcade.software/share/yMGC6mxvf8PPQi0f2ePp?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/profiles/contractors': {
        title: 'Contractors',
        description: 'Manage owner-operator contracts, settlements, insurance, and communication in one place.',
        videoId: '31Dqrvn9dnw',
        videoUrl: 'https://app.arcade.software/share/EQuanD4C9AnQJ5SKNOvh?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/profiles/trucks': {
        title: 'Trucks',
        description: 'View and edit truck details — VIN, registration, ELD assignment, and maintenance history.',
        videoId: 'rJ5CLe-Bd34',
    },
    '/profiles/trailers': {
        title: 'Trailers',
        description: 'Track trailer inventory, assignments, inspection records, and current location.',
        videoId: '4jvfaSdfVdM',
    },
    '/profiles/assets': {
        title: 'Assets',
        description: 'Track and manage your trucks, trailers, and other fleet assets in one place.',
        videoUrl: 'https://app.arcade.software/share/rsgEKpk6dAGIEfXgQO5p?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
    '/profiles/brokers': {
        title: 'Brokers',
        description: 'Manage broker contacts with MC numbers, credit scores, payment terms, and lane history.',
        videoId: 'BAlMqFIM98w',
        videoUrl: 'https://app.arcade.software/share/2QrdmrSkE5OaYb3lP9ub?embed&embed_mobile=tab&embed_desktop=inline&show_copy_link=true',
    },
}

function getTourKey(appId: string, userId: string, path: string): string {
    return `numeo:pageIntroTour:v1:${appId}:${userId}:${path}`
}

export const TmsPageTourOverlay: FC = () => {
    const { pathname } = useLocation()
    const { application, user } = useAuth()
    const { role } = useUserRole()
    const [visible, setVisible] = useState(false)

    const pageInfo = PAGE_TOURS[pathname]
    const appId = application?.id ?? ''
    const userId = user?.id ?? ''
    const storageKey = pageInfo && appId && userId ? getTourKey(appId, userId, pathname) : ''

    useEffect(() => {
        if (!pageInfo || role !== 'tmc' || !appId || !userId) {
            setVisible(false)
            return
        }
        // Stand down while the one-time category intro modal owns this route, so the two
        // onboarding surfaces never stack. The page tour reappears on the next visit once
        // the category intro has been seen or dismissed.
        if (isCategoryIntroPending({ pathname, role, appId, userId })) {
            setVisible(false)
            return
        }
        try {
            const toursEnabled = localStorage.getItem('numeo:tmsPageToursEnabled') === 'true'
            if (!toursEnabled) {
                setVisible(false)
                return
            }
            const seen = localStorage.getItem(storageKey) === 'true'
            setVisible(!seen)
        } catch {
            setVisible(false)
        }
    }, [pathname, role, appId, userId, storageKey, pageInfo])

    const dismiss = () => {
        setVisible(false)
        try {
            if (storageKey) localStorage.setItem(storageKey, 'true')
        } catch {
            // ignore
        }
    }

    if (!visible || !pageInfo) return null

    return (
        <TmsPageIntroCard
            title={pageInfo.title}
            description={pageInfo.description}
            videoId={pageInfo.videoId}
            videoUrl={pageInfo.videoUrl}
            onDismiss={dismiss}
        />
    )
}
